<!--
  StorefrontProductGrid.vue -- P5 storefront (shared product grid).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P5 -- Home + Category pages + route tree (frontend).

  Renders the ORDERED product summaries the P1 /category endpoint returns
  VERBATIM -- the array is already in StoreCategoryProduct.sort_order (the
  order the categories editor writes). This component never re-sorts.

  Product summary shape (from _product_summary in storefront_products.py):
    { id, name, price_cents, thumbnail_url, colors: [{ id, name, hex }] }

  Each card is lean (the buy box lives on the P6 product page):
    - image: the product thumbnail when present, else a tinted GarmentSilhouette
      (generic garment class -- the summary carries no silhouette field --
      tinted to the FIRST color). No decoration-included badge.
    - name
    - BASE price via salesPriceFormat(price_cents / 100) (base garment price,
      NO decoration bundling).
    - color swatches (up to a small cap; a "+N" chip when there are more).

  Each card links to the P6 product page (/shop/<storeSlug>/c/<categorySlug>/
  p/<id>), registered by P6 in router/groups/storefront.js.

  When categorySlug is empty (Home), the same shape is used and the URL
  carries an EMPTY /c/ segment (/shop/<storeSlug>/c//p/<id>) -- the home
  category's slug genuinely is "" (Home lives at /shop/<storeSlug> and owns
  no /c/ segment of its own). That URL resolves because the product route
  declares `c/:categorySlug([^/]*)/p/:id`: the custom-regex param matches the
  empty string, where a bare `:categorySlug` would not.

  That last part was WRONG here until 2026-08-18. This comment used to assert
  the empty segment resolved "matching the backend's empty-slug = home
  semantics" -- true of the backend, false of the frontend router, which
  required a non-empty param. So every card on the Home grid 404'd. Keep this
  comment and the route regex in agreement; a comment documenting behavior the
  code does not have is how that shipped broken.
-->
<template>
  <div class="sf-product-grid" data-testid="sf-product-grid">
    <ul class="sf-grid" data-testid="sf-grid">
      <li
        v-for="product in products"
        :key="product.id"
        class="sf-grid-item"
        :data-testid="`sf-grid-item-${product.id}`"
      >
        <!-- Two handles on the same card link: `sf-card-<id>` targets a
             specific product; the shared `sf-card` (on the body inside this
             link) lets automation click "the first product card" without
             knowing the DB-autoincrement product id, which varies across
             sandboxes. Both are attribute-only -- no behavior change. -->
        <router-link
          :to="productPath(product)"
          class="sf-card"
          :data-testid="`sf-card-${product.id}`"
        >
          <div class="sf-card__media" data-testid="sf-card-media">
            <img
              v-if="product.thumbnail_url"
              :src="product.thumbnail_url"
              :alt="product.name"
              class="sf-card__thumb"
              data-testid="sf-card-thumb"
            />
            <garment-silhouette
              v-else
              :color="firstColor(product)"
              class="sf-card__silhouette"
              data-testid="sf-card-silhouette"
            />
          </div>

          <div class="sf-card__body" data-testid="sf-card">
            <div
              class="sf-card__name"
              :title="product.name"
              data-testid="sf-card-name"
            >
              {{ product.name }}
            </div>
            <div class="sf-card__price" data-testid="sf-card-price">
              {{ priceLabel(product) ?? pricePlaceholder }}
            </div>

            <div
              v-if="hasColors(product)"
              class="sf-card__swatches"
              data-testid="sf-card-swatches"
            >
              <span
                v-for="color in visibleColors(product)"
                :key="color.id"
                class="sf-swatch"
                :style="{ background: swatchHex(color) }"
                data-testid="sf-card-swatch"
              >
                <!-- Color name on hover via a styled tooltip, replacing the
                     laggy native ``title`` attribute. ``activator="parent"``
                     targets the swatch dot itself, so it stays the hover
                     target and keeps its data-testid + inline background. -->
                <v-tooltip activator="parent" location="top">
                  {{ color.name }}
                </v-tooltip>
              </span>
              <span
                v-if="extraColorCount(product) > 0"
                class="sf-swatch-more"
                data-testid="sf-card-swatch-more"
                >+{{ extraColorCount(product) }}</span
              >
            </div>
          </div>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import GarmentSilhouette from "./GarmentSilhouette.vue";
import {
  CURRENCY_EMPTY_PLACEHOLDER,
  formatCurrencyNumber,
} from "@/utils/currency";
import { fromPriceLabel } from "@/pages/distributor/store-editor/StoreProducts/pricingMode";

// Neutral swatch when a color row carries no hex (SanMar vendor_color_hex is
// nullable). Matches GarmentSilhouette's own default tint.
const SWATCH_FALLBACK = "#c9d3db";

// How many swatches render before the "+N" overflow chip. Keeps the card
// compact; the full color list lives on the product page (P6).
const MAX_SWATCHES = 5;

export default {
  name: "StorefrontProductGrid",

  components: {
    "garment-silhouette": GarmentSilhouette,
  },

  props: {
    // Ordered product summaries from the P1 /category payload. Rendered
    // verbatim (no client sort).
    products: {
      type: Array,
      default: () => [],
    },
    // The store slug -- product links nest under it.
    storeSlug: {
      type: String,
      default: "",
    },
    // The category slug the product lives under (empty = Home). Composes the
    // P6 product URL.
    categorySlug: {
      type: String,
      default: "",
    },
  },

  computed: {
    pricePlaceholder() {
      return CURRENCY_EMPTY_PLACEHOLDER;
    },
  },

  methods: {
    hasColors(product) {
      return Array.isArray(product.colors) && product.colors.length > 0;
    },

    firstColor(product) {
      if (!this.hasColors(product)) {
        return { name: "", hex: SWATCH_FALLBACK };
      }
      const c = product.colors[0];
      return { name: c.name || "", hex: this.swatchHex(c) };
    },

    swatchHex(color) {
      return color && color.hex ? color.hex : SWATCH_FALLBACK;
    },

    visibleColors(product) {
      if (!this.hasColors(product)) return [];
      return product.colors.slice(0, MAX_SWATCHES);
    },

    extraColorCount(product) {
      if (!this.hasColors(product)) return 0;
      return Math.max(0, product.colors.length - MAX_SWATCHES);
    },

    // Base garment price. price_cents is whole cents; salesPriceFormat wants
    // a dollar amount. NO decoration bundling (base price only, per P5).
    //
    // Null when the server sent no price: `price_cents` is NULLABLE under
    // two-mode pricing (2026-09-29), and Number(null) is 0, so reading it
    // naively showed "$0.00". The card then shows a placeholder.
    basePrice(product) {
      const raw = product.price_cents;
      if (raw === null || raw === undefined) return null;
      const cents = Number(raw);
      if (!Number.isFinite(cents) || cents < 0) return null;
      const formatted = formatCurrencyNumber(cents / 100);
      return formatted === "—" ? null : formatted;
    },

    // The card's price text, or null (placeholder). A per-size quote
    // (`price_preview`, when the payload carries one) goes through the SAME
    // "From" rule as every editor surface, so a product whose sizes differ
    // reads "From $X" everywhere (#3884 gate P2-6). Without one, the flat
    // "from" price_cents is all there is.
    priceLabel(product) {
      const quoted = fromPriceLabel(product.price_preview);
      if (quoted !== null) return quoted;
      const base = this.basePrice(product);
      return base === null ? null : `$${base}`;
    },

    // P6 product URL. Home's category slug is empty, so this composes an
    // empty /c/ segment (/shop/<store>/c//p/<id>). That resolves because the
    // product route's param is declared `:categorySlug([^/]*)` -- it matches
    // the empty string. A bare `:categorySlug` does NOT, which is exactly why
    // every Home card 404'd before 2026-08-18. If that route regex is ever
    // narrowed, this method breaks for Home; the pair is load-bearing.
    productPath(product) {
      return `/shop/${this.storeSlug}/c/${this.categorySlug}/p/${product.id}`;
    },
  },
};
</script>

<style scoped>
.sf-product-grid {
  /* Callers (HomePage, CategoryPage) now nest this inside their own
     width-capped .sf-container, so the grid itself owns no side padding --
     a second padding here would double up against the container's. Kept as
     a hook class (still present in the DOM) rather than removed, in case a
     future caller mounts the grid outside a container. */
  padding: 0;
}

.sf-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /* auto-fit (not auto-fill): with FEWER products than fit a row, auto-fill
     keeps the empty tracks in the grid (so 1fr can't claim their space) and
     the row stops mid-page with a dead zone to the right -- the exact bug
     in the 2026-08-14 layout gap audit (3 products at 1280px). auto-fit
     collapses unused tracks, so the same 3 products' columns actually
     stretch to fill the row -- a deliberate, evenly-distributed line
     instead of a partial one.

     THE 360px CAP MOVED TO THE ITEM (2026-08-28). It used to be the track's
     upper bound -- `minmax(220px, 360px)` -- which capped the card but also
     capped the GRID: a rigid maximum leaves leftover inline space that no
     track can absorb, so at wide viewports the row simply stopped adding
     usable width, and at mid widths (768px, 1024px) it produced FEWER
     columns than fit (measured: 1 column in 704px of usable space, which
     comfortably holds 3). Capping the ITEM instead (.sf-grid-item below)
     keeps the same visual promise -- a card never balloons -- while letting
     the track count follow the available width.

     `min(220px, 100%)` rather than a bare 220px: below 220px of usable
     width a fixed minimum makes the track wider than its container and the
     row overflows the viewport horizontally. The min() clamps the floor to
     the container on narrow phones. */
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  justify-content: center;
  gap: 20px;
}

.sf-grid-item {
  margin: 0;
  /* The 2026-08-14 guard, now expressed on the item (see .sf-grid above).
     A category with 1-2 products must not stretch them into oversized
     cards: the tracks are free to grow, the CARD is not. `width: 100%`
     makes the item fill its track up to that cap, and `justify-self:
     center` centres it inside a track wider than the cap, so a short row
     stays visually balanced instead of hugging the left of each track. */
  max-width: 360px;
  width: 100%;
  justify-self: center;
  /* Grid items stretch to the row height; making the item a flex container
     lets the card (below) fill it, so every card in a row ends at the same
     height instead of sizing to its own content (GH #4288). */
  display: flex;
}

.sf-card {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  background: #fff;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.15s ease;
}

.sf-card:hover {
  box-shadow: var(--tf-elev-2, 0 3px 6px rgba(0, 0, 0, 0.12));
}

.sf-card__media {
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  /* WHITE, not --tf-surface-light (#f5f8fa).
   *
   * Every product photo in this catalog has a white background, and a
   * non-square photo `contain`ed in this square box letterboxes -- so a
   * tinted surface showed pale blue-grey bars either side of a white photo,
   * a visible seam where the eye expects none. White makes the photo's own
   * background continue into the surface.
   *
   * NOT a general "storefront surfaces are white" rule: the content cards
   * that use this token (the Home welcome card, the Category richtext card,
   * the PDP note box) KEEP the tint, where separating a block of text from
   * the page is exactly what it is for. */
  background: #fff;
  /* Clip anything that exceeds the 1:1 box. Mobile WebKit does not reliably
   * clamp a flex child by max-height alone against an aspect-ratio box, so
   * without this the thumb bled past the card on iOS. min-height:0 lets the
   * box actually honor the aspect ratio instead of growing to its content. */
  overflow: hidden;
  min-height: 0;
}

.sf-card__thumb {
  /* Fill the media box in both axes and contain within it (letterboxed,
   * whole garment shown, never cropped or stretched). Explicit 100% height +
   * width clamps reliably on mobile where max-height:100% did not. */
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sf-card__silhouette {
  width: 100%;
  height: 100%;
}

.sf-card__body {
  padding: 12px 14px 16px;
  /* Fills the card so the swatch row can pin to the bottom (GH #4288). */
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sf-card__name {
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
  line-height: 1.3;
  /* Reserve exactly two lines and clamp longer names, so a one-line and a
     two-line name give the same card height and prices align (GH #4288). */
  min-height: calc(2 * 1.3em);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.sf-card__price {
  font-size: 16px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
}

.sf-card__swatches {
  display: flex;
  align-items: center;
  gap: 5px;
  /* Never wraps (the chip count is capped at MAX_SWATCHES + "+N"), and sits
     at the card bottom so rows align whatever the swatch count (GH #4288). */
  flex-wrap: nowrap;
  min-height: 16px;
  margin-top: auto;
  padding-top: 2px;
}

.sf-swatch {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid var(--tf-border-strong, #d6d6d6);
  display: inline-block;
}

.sf-swatch-more {
  font-size: 12px;
  color: var(--tf-fg-3, #616161);
}
</style>
